import type { BookingTypeValue, CreateOrderBookingRequest, OrderBooking } from '@my/tms-api';
import type { BookingFormValues, BookingTypeShape } from './types';

/**
 * The booking-level fields of a create body, from the form. Empty strings
 * become null so the BE clears rather than rejects; drivers and vehicle only
 * travel on an accompanied booking.
 */
export function bookingFieldsFrom(
  values: BookingFormValues,
): Omit<CreateOrderBookingRequest, 'orderId' | 'departure' | 'arrival'> {
  return {
    bookingType: values.bookingType,
    isAccompanied: values.isAccompanied,
    sailDate: values.sailDate || null,
    sailTime: values.sailTime || null,
    carrierPartyId: values.carrierPartyId || null,
    agentPartyId: values.agentPartyId || null,
    carrierRouteId: values.carrierRouteId || null,
    routeCode: values.routeCode.trim() || null,
    routeName: values.routeName.trim() || null,
    typicalDurationMinutes: values.typicalDurationMinutes,
    typicalCheckInMinutes: values.typicalCheckInMinutes,
    typicalDriveOffMinutes: values.typicalDriveOffMinutes,
    carrierReference: values.carrierReference.trim() || null,
    vessel: values.vessel.trim() || null,
    voyageNumber: values.voyageNumber.trim() || null,
    numberOfDrivers: values.isAccompanied
      ? Number.parseInt(values.numberOfDrivers, 10) || null
      : null,
    vehicleRegistration: values.isAccompanied ? values.vehicleRegistration.trim() || null : null,
    trailerNumber: values.trailerNumber.trim() || null,
    customsReference: values.customsReference.trim() || null,
    clearanceStatus: values.clearanceStatus === '' ? null : values.clearanceStatus,
    notes: values.notes.trim() || null,
  };
}

/** Operator-facing labels for the `BookingType` BE enum. */
export const BOOKING_TYPE_OPTIONS: Array<{ value: BookingTypeValue; label: string }> = [
  { value: 'Artic', label: 'Artic' },
  { value: 'LorryOver6t', label: 'Lorry (over 6t)' },
  { value: 'VanUnder6t', label: 'Van (under 6t)' },
  { value: 'Trailer', label: 'Unaccompanied trailer' },
  { value: 'SoloTractor', label: 'Solo tractor' },
  { value: 'RoadTrain', label: 'Road train' },
  { value: 'Driver', label: 'Driver only' },
  { value: 'Container20to29', label: 'Container 20–29ft' },
  { value: 'Container30to39', label: 'Container 30–39ft' },
  { value: 'Container40to49', label: 'Container 40–49ft' },
];

/** The Ferry side of the form: blank until a Ferry-linked crossing is picked. */
const FERRY_FORM_DEFAULTS = {
  ferryBook: true,
  ferryBookingId: '',
  ferryCarrierCode: '',
  ferryCarrierId: '',
  ferryRouteCode: '',
  ferryOriginZone: '',
  ferryCredentialId: '',
  ferryCredentialRequired: false,
  ferrySailingUtc: '',
  ferryDayDepartures: '',
  ferryVehicleTypeCode: '',
  ferryLengthMetres: '',
  // Zero, as the old desktop sent: the carrier then books the vehicle type's own weight.
  ferryCargoWeightKg: '0',
  ferryDangerousGoods: false,
  ferryPlugIn: false,
  ferryPluginTemperature: '',
  ferryUkCustomsProcedure: '' as const,
  ferryExportId: '',
  ferryImportId: '',
};

export const EMPTY_BOOKING_FORM_VALUES: BookingFormValues = {
  sailDate: '',
  sailTime: '',
  bookingType: 'Artic',
  departureAddressId: '',
  arrivalAddressId: '',
  carrierRouteId: '',
  routeCode: '',
  routeName: '',
  typicalDurationMinutes: null,
  typicalCheckInMinutes: null,
  typicalDriveOffMinutes: null,
  carrierPartyId: '',
  agentPartyId: '',
  carrierReference: '',
  vessel: '',
  voyageNumber: '',
  isAccompanied: true,
  numberOfDrivers: '',
  vehicleRegistration: '',
  trailerNumber: '',
  customsReference: '',
  clearanceStatus: '',
  notes: '',
  ...FERRY_FORM_DEFAULTS,
};

export function bookingTypeShape(t: BookingTypeValue): BookingTypeShape {
  switch (t) {
    case 'Artic':
    case 'RoadTrain':
      return {
        hasDrivers: true,
        hasVehicle: true,
        hasTrailer: true,
        trailerLabel: 'Trailer number',
        forceUnaccompanied: false,
      };
    case 'LorryOver6t':
    case 'VanUnder6t':
    case 'SoloTractor':
      return {
        hasDrivers: true,
        hasVehicle: true,
        hasTrailer: false,
        trailerLabel: 'Trailer number',
        forceUnaccompanied: false,
      };
    case 'Trailer':
      return {
        hasDrivers: false,
        hasVehicle: false,
        hasTrailer: true,
        trailerLabel: 'Trailer number',
        forceUnaccompanied: true,
      };
    case 'Container20to29':
    case 'Container30to39':
    case 'Container40to49':
      // Accompanied stays editable — RoRo intermodal (driver crosses
      // with truck+trailer+container) and LoLo container-only are
      // both legitimate.
      return {
        hasDrivers: true,
        hasVehicle: true,
        hasTrailer: true,
        trailerLabel: 'Container BIC',
        forceUnaccompanied: false,
      };
    case 'Driver':
      return {
        hasDrivers: true,
        hasVehicle: false,
        hasTrailer: false,
        trailerLabel: 'Trailer number',
        forceUnaccompanied: false,
      };
  }
}

/**
 * Patch for a booking-type swap. Clears orphan values that the new
 * shape doesn't carry; forces Unaccompanied when the new type is
 * by-definition unaccompanied (Trailer).
 */
export function applyBookingTypeChange(newType: BookingTypeValue): Partial<BookingFormValues> {
  const shape = bookingTypeShape(newType);
  const patch: Partial<BookingFormValues> = { bookingType: newType };
  if (shape.forceUnaccompanied) patch.isAccompanied = false;
  if (!shape.hasDrivers) patch.numberOfDrivers = '';
  if (!shape.hasVehicle) patch.vehicleRegistration = '';
  if (!shape.hasTrailer) patch.trailerNumber = '';
  // The carrier's vehicle type follows the booking type; cleared so it is picked again.
  patch.ferryVehicleTypeCode = '';
  patch.ferryLengthMetres = '';
  return patch;
}

/** Hydrate form values from a BE OrderBooking + its two paired tasks. */
export function bookingFormValuesFromSource(source: OrderBooking): BookingFormValues {
  const departureTask = source.tasks.find((t) => t.type === 'BookingDeparture');
  const arrivalTask = source.tasks.find((t) => t.type === 'BookingArrival');
  return {
    sailDate: source.sailDate ?? '',
    sailTime: source.sailTime ?? '',
    bookingType: source.bookingType,
    departureAddressId: departureTask?.addressId ?? '',
    arrivalAddressId: arrivalTask?.addressId ?? '',
    carrierRouteId: source.carrierRouteId ?? '',
    routeCode: source.routeCode ?? '',
    routeName: source.routeName ?? '',
    typicalDurationMinutes: source.typicalDurationMinutes ?? null,
    typicalCheckInMinutes: source.typicalCheckInMinutes ?? null,
    typicalDriveOffMinutes: source.typicalDriveOffMinutes ?? null,
    carrierPartyId: source.carrierPartyId ?? '',
    agentPartyId: source.agentPartyId ?? '',
    carrierReference: source.carrierReference ?? '',
    vessel: source.vessel ?? '',
    voyageNumber: source.voyageNumber ?? '',
    isAccompanied: source.isAccompanied,
    numberOfDrivers: source.numberOfDrivers != null ? String(source.numberOfDrivers) : '',
    vehicleRegistration: source.vehicleRegistration ?? '',
    trailerNumber: source.trailerNumber ?? '',
    customsReference: source.customsReference ?? '',
    clearanceStatus: source.clearanceStatus ?? '',
    notes: source.notes ?? '',
    ...FERRY_FORM_DEFAULTS,
    ferryBookingId: source.ferryBookingId ?? '',
  };
}
